import { Table, Button, Popover, message } from "antd";
import { CopyOutlined, CheckCircleTwoTone } from "@ant-design/icons";
import { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import DuplicatePrice from './DuplicatePrice';
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import ViewButton from "../UI/ViewButton/viewbutton";
import DeletePrice from "./DeletePrice";
import { useMutation } from "react-query";
import { activatePriceList, editPriceList } from "../../api/priceLists";
import { PriceListsInterface } from "./Interfaces";

type prop = {
  tableKey: number;
  saleLists: PriceListsInterface[];
  setSaleLists: React.Dispatch<React.SetStateAction<PriceListsInterface[]>>;
  setSaleListsSearch: React.Dispatch<React.SetStateAction<PriceListsInterface[]>>;
  isLoadingProp: boolean;
}

const PriceList = ({ tableKey, saleLists, setSaleLists, isLoadingProp, setSaleListsSearch }: prop) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const [isDuplicatePriceOpen, setIsDuplicatePriceOpen] = useState(false);
  const [itemToDuplicate, setItemToDuplicate] = useState<{
    name: string,
    id: number
  }>({
    name: '',
    id: 0
  });
  const [isDeleteItemOpen, setIsDeleteItemOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    name: string,
    id: number
  }>({
    name: '',
    id: 0
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>();
  const [selectedEditRow, setSelectedEditRow] = useState<number | undefined>();
  const [editedName, setEditedName] = useState("");

  const { mutateAsync: activatePriceListMutation } = useMutation(
    "activatePriceList",
    ({ id }: { id: React.Key[] }) =>
      activatePriceList({ id }),
    {
      onMutate: () => {
        message.loading('Activating price list...')
      },
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.data.message)
      },
    }
  );

  const { mutateAsync: editPriceListMutation } = useMutation(
    "editPriceList",
    ({ id, editedName, warehouseId }: { id: number, editedName: string, warehouseId: number }) =>
      editPriceList({ id, editedName, warehouseId }),
    {
      onMutate: () => {
        message.loading('loading...')
      },
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setSaleLists(data.data)
        setSaleListsSearch(data.data)
        setSelectedEditRow(undefined)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.data.message)
      },
    }
  );

  const handleDelete = (id: number, name: string): void => {
    setItemToDelete({ name, id });
    setIsDeleteItemOpen(true);
  };

  const handleDuplicate = (id: number, name: string) => {
    setItemToDuplicate({ name, id })
    setIsDuplicatePriceOpen(true)
  }

  const HandleEdit = (id: number, name: string) => {
    setSelectedEditRow(id)
    setEditedName(name)
  }

  const handleEditSave = (id: number, name: string, editedName: string, warehouseId: number) => {
    if (name === editedName) {
      setSelectedEditRow(undefined)
      return
    }
    editPriceListMutation({ id, editedName, warehouseId })
  }

  useLayoutEffect(() => {
    const activeList = [saleLists.find(record => record.isActivated)?.id].map(id => id!).filter(Boolean);
    setSelectedRowKeys(activeList)
  }, [saleLists])

  const handleSelectedRowChange = (selectedKeys: React.Key[], row: any) => {
    setSelectedRowKeys(selectedKeys);
    activatePriceListMutation({ id: selectedKeys })
  };

  const columns = [
    {
      title: `${t("list-name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
      render: (name: string, record: PriceListsInterface) => {
        return selectedEditRow === record.id ?
          <input
            type="text"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            placeholder={name}
          />
          : <div>{name}</div>
      }
    },
    {
      title: `${t("start-date")}`,
      dataIndex: "startDate",
      key: "activedate",
      width: "10rem",
      render: (date: any) => {
        return format(new Date(date), "dd/MM/yyyy");
      },
    },
    {
      title: `${t("end-date")}`,
      dataIndex: "endDate",
      key: "exdate",
      width: "10rem",
      render: (date: any) => {
        return format(new Date(date), "dd/MM/yyyy");
      },
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "8rem",
      align: "center" as "center",
      render: (record: any) => (
        <>
          <Popover content={<div>{t("duplicate-list")}</div>} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              icon={
                <CopyOutlined style={{ color: "#212121", fontSize: "14px" }} />
              }
              size={"small"}
              type="text"
              onClick={() => handleDuplicate(record?.id, record?.name)}
            />
          </Popover>
          <ViewButton
            onClick={() => {
              const { id, name } = record
              navigate(`/priceList/details/${id}?title=${name}`);
            }}
          />
          {selectedEditRow === record.id ? <Popover content={t('confirm-changes')} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              type="text"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "14px" }} />}
              onClick={() => handleEditSave(record.id, record.name, editedName, record.warehouse.id)}
              size={"small"}
            />
          </Popover> : <EditButton onClick={() => HandleEdit(record?.id, record?.name)} />}

          <DeleteButton
            onClick={() => {
              handleDelete(record?.id, record?.name);
            }}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <DeletePrice
        open={isDeleteItemOpen}
        setOpen={setIsDeleteItemOpen}
        itemToDelete={itemToDelete}
        setSaleLists={setSaleLists}
        setSaleListsSearch={setSaleListsSearch}
      />
      <Table
        scroll={{ x: 1000 }}
        key={tableKey}
        columns={columns}
        dataSource={saleLists}
        rowKey={(record) => record.id}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        loading={isLoadingProp}
        rowSelection={{
          selectedRowKeys, onChange: handleSelectedRowChange, type: 'radio', columnTitle: `${t("active")}`,
          columnWidth: '5rem'
        }}
      />
      <DuplicatePrice itemToDuplicate={itemToDuplicate} isDuplicatePriceOpen={isDuplicatePriceOpen} setIsDuplicatePriceOpen={setIsDuplicatePriceOpen} setSaleLists={setSaleLists} setSaleListsSearch={setSaleListsSearch} />
    </>
  );
};

export default PriceList;
